import {
  LOADING_RETRO_BOARDS,
  LOAD_RETRO_BOARDS,
  ERROR_RETRO_BOARDS,
  LOADING_RETRO_BOARD_DETAIL,
  LOAD_RETRO_BOARD_DETAIL,
  ERROR_RETRO_BOARD_DETAIL,
  RESET_RETRO_CURRENT_BOARD,
  SET_RETRO_TABLERO,
  SET_RETRO_VOTANTES,
  ADD_RETRO_CARD,
  REMOVE_RETRO_CARD,
  RESTORE_RETRO_CARD,
  PATCH_RETRO_CARD,
  SET_RETRO_COLUMN_CARDS,
} from "./retroBoardTypes";
import { RetroBoardServices } from "../../services/retroBoard";

// Thunks del Tablero de Retrospectiva (spec 006, plan.md §3.2). Molde de fetch simple:
// redux/briefcaseArtifacts/briefcaseArtifactsActions.js (LOADING_X/LOAD_X/ERROR_X). Los thunks de
// escritura (crear/editar/borrar tarjeta, reordenar, votar, dar like) además:
//  - actualizan el estado local ANTES de llamar al backend (optimista, sin tiempo real, RF-16/6.1),
//  - devuelven la promesa del service para que el componente pueda encadenar/mostrar feedback,
//  - revierten el patch optimista si el backend rechaza el cambio (409 NO_VOTE_BUDGET / BOARD_CLOSED, o
//    cualquier otro error), re-lanzando el error para que el componente decida el mensaje (services/
//    retroBoard.js expone `getRetroErrorCode` para leer el `code` del 409).

// ---------------------------------------------------------------------------------------------------
// Listado de tableros + detalle de un tablero
// ---------------------------------------------------------------------------------------------------

export const fetchRetroBoards = (idPortafolio) => (dispatch) => {
  dispatch({ type: LOADING_RETRO_BOARDS });
  return RetroBoardServices.listBoards(idPortafolio)
    .then((data) => dispatch({ type: LOAD_RETRO_BOARDS, payload: data }))
    .catch(() => dispatch({ type: ERROR_RETRO_BOARDS }));
};

// Carga metadata + columnas + tarjetas + votantes de un tablero en paralelo. La metadata del tablero se
// pide FRESCA (listBoards) en la misma tanda —NO se toma del `boards` cacheado— porque el estado del tablero
// cambia en el servidor sin que el front lo provoque: el auto-cierre perezoso al vencer el timer (RF-27)
// pasa el tablero a CERRADO dentro del propio SP de listado. Leerlo del cache dejaba el header desincronizado
// (badge "Abierto" + botón "Cerrar" visibles aunque en BD ya estaba CERRADO, y "Cerrar" devolvía error). De
// paso se refresca el listado (LOAD_RETRO_BOARDS) para que la vista de lista quede consistente.
export const fetchRetroBoardDetail = (idPortafolio, idTablero) => (dispatch) => {
  dispatch({ type: LOADING_RETRO_BOARD_DETAIL });
  return Promise.all([
    RetroBoardServices.listBoards(idPortafolio),
    RetroBoardServices.listColumns(idPortafolio),
    RetroBoardServices.listCards(idPortafolio, idTablero),
    RetroBoardServices.listVoters(idPortafolio, idTablero),
  ])
    .then(([boards, columnas, tarjetas, votantes]) => {
      const tablero = (boards || []).find((b) => b.IdTablero === idTablero) || null;
      dispatch({ type: LOAD_RETRO_BOARDS, payload: boards });
      dispatch({
        type: LOAD_RETRO_BOARD_DETAIL,
        payload: { tablero, columnas, tarjetas, votantes },
      });
    })
    .catch(() => dispatch({ type: ERROR_RETRO_BOARD_DETAIL }));
};

export const resetRetroCurrentBoard = () => ({ type: RESET_RETRO_CURRENT_BOARD });

// ---------------------------------------------------------------------------------------------------
// Tablero (RF-1..RF-5)
// ---------------------------------------------------------------------------------------------------

export const createRetroBoard = (idPortafolio, payload) => (dispatch) =>
  RetroBoardServices.createBoard(idPortafolio, payload).then((data) => {
    dispatch(fetchRetroBoards(idPortafolio));
    return data;
  });

export const updateRetroBoard = (idPortafolio, idTablero, payload) => (dispatch) =>
  RetroBoardServices.updateBoard(idPortafolio, idTablero, payload).then((data) => {
    dispatch(fetchRetroBoards(idPortafolio));
    return data;
  });

export const closeRetroBoard = (idPortafolio, idTablero) => (dispatch, getState) =>
  RetroBoardServices.closeBoard(idPortafolio, idTablero).then((data) => {
    const current = getState().retroBoard.currentBoard.tablero;
    if (current && current.IdTablero === idTablero) {
      dispatch({ type: SET_RETRO_TABLERO, payload: { ...current, Estado: "CERRADO" } });
    }
    dispatch(fetchRetroBoards(idPortafolio));
    return data;
  });

// RF-23/CA-16 (incremento post-cierre): reabre un tablero CERRADO. Permiso restringido — el SP es la
// defensa real (CreadoPor del tablero o Gerente del proyecto/ciclo asociado); el front solo hace gate de
// UX (RetroBoard.jsx). Sin update optimista (es una acción de estado del tablero, no de tarjeta): al
// éxito se refresca el detalle completo (fetchRetroBoardDetail ya trae el tablero fresco y refresca el
// listado, así que vuelve a ABIERTO y readOnly=false sin un fetch extra).
export const reopenRetroBoard = (idPortafolio, idTablero) => (dispatch) =>
  RetroBoardServices.reopenBoard(idPortafolio, idTablero).then((data) => {
    dispatch(fetchRetroBoardDetail(idPortafolio, idTablero));
    return data;
  });

// RF-25 (Incremento 2): inicia la ceremonia (timer). Mismo patrón que reopenRetroBoard — sin update
// optimista (es un estado del tablero, no de una tarjeta): al éxito se refresca el detalle completo
// (trae TimerInicioUtc/TimerDuracionMinutos frescos para que el front arranque el countdown, y de paso
// el listado).
export const startRetroTimer = (idPortafolio, idTablero) => (dispatch) =>
  RetroBoardServices.startTimer(idPortafolio, idTablero).then((data) => {
    dispatch(fetchRetroBoardDetail(idPortafolio, idTablero));
    return data;
  });

export const deleteRetroBoard = (idPortafolio, idTablero) => (dispatch) =>
  RetroBoardServices.deleteBoard(idPortafolio, idTablero).then((data) => {
    dispatch(fetchRetroBoards(idPortafolio));
    return data;
  });

// ---------------------------------------------------------------------------------------------------
// Tarjetas (RF-7..RF-11) — optimistas
// ---------------------------------------------------------------------------------------------------

const makeTempId = () => `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// currentUserId: localStorage.getItem("UID") (patrón ya usado en el repo, ej. views/Landing.jsx) —
// solo para pintar la autoría de inmediato; el backend es la fuente de verdad tras confirmar.
export const createRetroCard = (idPortafolio, idTablero, idColumna, codigoColumna, texto, currentUserId) => (
  dispatch,
  getState
) => {
  const tempId = makeTempId();
  const tarjetasColumna = getState().retroBoard.currentBoard.tarjetas.filter(
    (t) => t.IdColumna === idColumna
  );
  const tempCard = {
    IdTarjeta: tempId,
    IdTablero: idTablero,
    IdColumna: idColumna,
    CodigoColumna: codigoColumna,
    Texto: texto,
    Orden: tarjetasColumna.length + 1,
    CreadoPor: currentUserId || null,
    FechaCreado: new Date().toISOString(),
    ModificadoPor: null,
    FechaModificado: null,
    TotalVotos: 0,
    TotalLikes: 0,
    MisVotos: 0,
    YoDiLike: false,
  };
  dispatch({ type: ADD_RETRO_CARD, payload: tempCard });

  return RetroBoardServices.createCard(idPortafolio, idTablero, { IdColumna: idColumna, Texto: texto })
    .then((data) => {
      dispatch({ type: REMOVE_RETRO_CARD, payload: { idTarjeta: tempId } });
      dispatch({ type: ADD_RETRO_CARD, payload: { ...tempCard, IdTarjeta: data.IdTarjeta } });
      return data;
    })
    .catch((err) => {
      dispatch({ type: REMOVE_RETRO_CARD, payload: { idTarjeta: tempId } });
      throw err;
    });
};

export const updateRetroCard = (idPortafolio, idTablero, idTarjeta, texto, currentUserId) => (
  dispatch,
  getState
) => {
  const card = getState().retroBoard.currentBoard.tarjetas.find((t) => t.IdTarjeta === idTarjeta);
  const prev = card ? { Texto: card.Texto, ModificadoPor: card.ModificadoPor, FechaModificado: card.FechaModificado } : null;

  dispatch({ type: PATCH_RETRO_CARD, payload: { idTarjeta, patch: { Texto: texto } } });

  return RetroBoardServices.updateCard(idPortafolio, idTablero, idTarjeta, { Texto: texto })
    .then((data) => {
      dispatch({
        type: PATCH_RETRO_CARD,
        payload: {
          idTarjeta,
          patch: { Texto: texto, ModificadoPor: currentUserId || null, FechaModificado: new Date().toISOString() },
        },
      });
      return data;
    })
    .catch((err) => {
      if (prev) dispatch({ type: PATCH_RETRO_CARD, payload: { idTarjeta, patch: prev } });
      throw err;
    });
};

export const deleteRetroCard = (idPortafolio, idTablero, idTarjeta) => (dispatch, getState) => {
  const tarjetas = getState().retroBoard.currentBoard.tarjetas;
  const index = tarjetas.findIndex((t) => t.IdTarjeta === idTarjeta);
  const card = tarjetas[index];

  dispatch({ type: REMOVE_RETRO_CARD, payload: { idTarjeta } });

  return RetroBoardServices.deleteCard(idPortafolio, idTablero, idTarjeta).catch((err) => {
    if (card) dispatch({ type: RESTORE_RETRO_CARD, payload: { card, index } });
    throw err;
  });
};

// Reorder vertical dentro de columna (RF-10/FA-3). `orderedTarjetas` ya viene con el nuevo orden de esa
// columna (el componente lo arma tras el onDragEnd de @hello-pangea/dnd).
export const reorderRetroCards = (idPortafolio, idTablero, idColumna, orderedTarjetas) => (
  dispatch,
  getState
) => {
  const previous = getState().retroBoard.currentBoard.tarjetas.filter(
    (t) => t.IdColumna === idColumna
  );
  const withOrden = orderedTarjetas.map((t, i) => ({ ...t, Orden: i + 1 }));

  dispatch({ type: SET_RETRO_COLUMN_CARDS, payload: { idColumna, tarjetas: withOrden } });

  const payload = {
    IdColumna: idColumna,
    Orden: withOrden.map((t) => ({ IdTarjeta: t.IdTarjeta, Orden: t.Orden })),
  };

  return RetroBoardServices.reorderCards(idPortafolio, idTablero, payload).catch((err) => {
    dispatch({ type: SET_RETRO_COLUMN_CARDS, payload: { idColumna, tarjetas: previous } });
    throw err;
  });
};

// ---------------------------------------------------------------------------------------------------
// Votos (RF-12..RF-16) — optimistas, rollback en 409 NO_VOTE_BUDGET / BOARD_CLOSED
// ---------------------------------------------------------------------------------------------------

export const addRetroVote = (idPortafolio, idTablero, idTarjeta) => (dispatch, getState) => {
  const card = getState().retroBoard.currentBoard.tarjetas.find((t) => t.IdTarjeta === idTarjeta);
  const prev = { TotalVotos: card ? card.TotalVotos : 0, MisVotos: card ? card.MisVotos : 0 };

  dispatch({
    type: PATCH_RETRO_CARD,
    payload: { idTarjeta, patch: { TotalVotos: prev.TotalVotos + 1, MisVotos: prev.MisVotos + 1 } },
  });

  return RetroBoardServices.addVote(idPortafolio, idTablero, idTarjeta)
    .then((data) => {
      dispatch({
        type: PATCH_RETRO_CARD,
        payload: { idTarjeta, patch: { TotalVotos: data.TotalVotos, MisVotos: data.MisVotos } },
      });
      return data;
    })
    .catch((err) => {
      dispatch({ type: PATCH_RETRO_CARD, payload: { idTarjeta, patch: prev } });
      throw err;
    });
};

export const removeRetroVote = (idPortafolio, idTablero, idTarjeta) => (dispatch, getState) => {
  const card = getState().retroBoard.currentBoard.tarjetas.find((t) => t.IdTarjeta === idTarjeta);
  const prev = { TotalVotos: card ? card.TotalVotos : 0, MisVotos: card ? card.MisVotos : 0 };
  const optimistic = {
    TotalVotos: Math.max(0, prev.TotalVotos - 1),
    MisVotos: Math.max(0, prev.MisVotos - 1),
  };

  dispatch({ type: PATCH_RETRO_CARD, payload: { idTarjeta, patch: optimistic } });

  return RetroBoardServices.removeVote(idPortafolio, idTablero, idTarjeta)
    .then((data) => {
      dispatch({
        type: PATCH_RETRO_CARD,
        payload: { idTarjeta, patch: { TotalVotos: data.TotalVotos, MisVotos: data.MisVotos } },
      });
      return data;
    })
    .catch((err) => {
      dispatch({ type: PATCH_RETRO_CARD, payload: { idTarjeta, patch: prev } });
      throw err;
    });
};

// ---------------------------------------------------------------------------------------------------
// Likes (RF-17) — toggle optimista
// ---------------------------------------------------------------------------------------------------

// Refresca SOLO la lista de "quién votó / quién dio like" (RF-18) sin recargar todo el detalle. El toggle
// optimista de like actualiza TotalLikes/YoDiLike de la tarjeta pero NO la lista de nombres; sin este
// refresco el tooltip de transparencia mostraba "nadie ha dado like" con el contador en 1 hasta el siguiente
// refetch completo (se veía sobre todo al cerrar la retro, que no recarga el detalle). Un fallo aquí no
// rompe el like (el contador ya quedó correcto), por eso el catch es silencioso.
const refreshRetroVoters = (idPortafolio, idTablero) => (dispatch) =>
  RetroBoardServices.listVoters(idPortafolio, idTablero)
    .then((votantes) => dispatch({ type: SET_RETRO_VOTANTES, payload: votantes }))
    .catch(() => {});

export const toggleRetroLike = (idPortafolio, idTablero, idTarjeta) => (dispatch, getState) => {
  const card = getState().retroBoard.currentBoard.tarjetas.find((t) => t.IdTarjeta === idTarjeta);
  const prev = {
    TotalLikes: card ? card.TotalLikes : 0,
    YoDiLike: card ? !!card.YoDiLike : false,
  };
  const optimisticYoDiLike = !prev.YoDiLike;
  const optimistic = {
    YoDiLike: optimisticYoDiLike,
    TotalLikes: Math.max(0, prev.TotalLikes + (optimisticYoDiLike ? 1 : -1)),
  };

  dispatch({ type: PATCH_RETRO_CARD, payload: { idTarjeta, patch: optimistic } });

  return RetroBoardServices.toggleLike(idPortafolio, idTablero, idTarjeta)
    .then((data) => {
      dispatch({
        type: PATCH_RETRO_CARD,
        payload: {
          idTarjeta,
          patch: { YoDiLike: data.estado === "LIKED", TotalLikes: data.TotalLikes },
        },
      });
      // Sincroniza la lista de votantes con el contador (evita el tooltip "nadie ha dado like" con el
      // contador en 1). No se encadena al return: es un refresco secundario, el like ya está confirmado.
      dispatch(refreshRetroVoters(idPortafolio, idTablero));
      return data;
    })
    .catch((err) => {
      dispatch({ type: PATCH_RETRO_CARD, payload: { idTarjeta, patch: prev } });
      throw err;
    });
};
